
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import sonomaLogo from "@/components/requiredlegaldocuments/landing/assets/sonoma-rounded.png";

interface FooterSettings {
  companyName: string;
  description: string;
  copyrightText: string;
  showSocialLinks: boolean;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactAddress?: string;
}

const LandingFooter = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState<FooterSettings>({
    companyName: "Sonoma Cloud",
    description: "Modern school management for the digital age.",
    copyrightText: `© ${currentYear} Sonoma Cloud. All rights reserved.`,
    showSocialLinks: true,
    facebookUrl: "",
    twitterUrl: "",
    instagramUrl: "",
    linkedinUrl: "",
    contactEmail: "hello@sonoma.com",
    contactPhone: "(555) 123-4567",
    contactAddress: "San Francisco, CA"
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, "systemConfig/landingPage");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<FooterSettings>;
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error("Error fetching footer settings:", error);
      }
    };

    fetchSettings();
  }, []);

  return (
      <footer className="border-t border-slate-200 dark:border-slate-800 py-12 lg:py-16 px-6 lg:px-8 bg-white dark:bg-[#020817] text-slate-950 dark:text-slate-50 transition-colors duration-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12 mb-12">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img 
                  src={sonomaLogo}
                  alt={settings.companyName} 
                  className="w-7 h-7 dark:drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]"
                />
                <span className="font-semibold">{settings.companyName}</span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-xs leading-relaxed">
                {settings.description}
              </p>
              {settings.showSocialLinks && (
                <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                  {settings.twitterUrl && (
                    <a href={settings.twitterUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" aria-label="Twitter">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  )}
                  {settings.linkedinUrl && (
                    <a href={settings.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" aria-label="LinkedIn">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                  {settings.facebookUrl && (
                      <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" aria-label="Facebook">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </a>
                  )}
                  {settings.instagramUrl && (
                      <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" aria-label="Instagram">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      </a>
                  )}
                </div>
              )}
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-medium text-sm mb-4">Product</h4>
              <ul className="space-y-2.5 text-sm text-slate-500 dark:text-slate-400">
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-features">Features</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-pricing">Pricing</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-integrations">Integrations</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-changelog">Changelog</a></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-medium text-sm mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm text-slate-500 dark:text-slate-400">
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-about">About</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-blog">Blog</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-careers">Careers</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-contact">Contact</a></li>
              </ul>
            </div>
            
            {/* Resources */}
            <div>
              <h4 className="font-medium text-sm mb-4">Resources</h4>
              <ul className="space-y-2.5 text-sm text-slate-500 dark:text-slate-400">
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-docs">Documentation</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-help">Help Center</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-api">API Reference</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-footer-status">Status</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-medium text-sm mb-4">Contact</h4>
              <ul className="space-y-2.5 text-sm text-slate-500 dark:text-slate-400">
                {settings.contactEmail && (
                    <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {settings.contactEmail}
                    </li>
                )}
                {settings.contactPhone && (
                    <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {settings.contactPhone}
                    </li>
                )}
                {settings.contactAddress && (
                    <li className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {settings.contactAddress}
                    </li>
                )}
              </ul>
            </div>
          </div>
          
          {/* Bottom */}
          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            <p>{settings.copyrightText}</p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-privacy">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-terms">Terms</Link>
              <a href="#" className="hover:text-slate-900 dark:hover:text-slate-50 transition-colors" data-testid="link-cookies">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
  );
};

export default LandingFooter;
